import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL as string;

if (!BASE_URL) {
    throw new Error('BASE_URL is not defined in the environment variables.');
}

export interface DictionaryItem {
    label: string;
    value: string;
}

export interface DictionaryName {
    name: string;
    code: string;
}

/**
 * Sanitize a string value by trimming whitespace and stripping HTML tags.
 */
export function sanitizeString(value: string): string {
    return value.trim().replace(/<[^>]*>/g, '');
}

/**
 * Deep-sanitize all string values in an object.
 */
export function sanitizeFormData<T>(data: T): T {
    if (typeof data === 'string') {
        return sanitizeString(data) as unknown as T;
    }
    if (Array.isArray(data)) {
        return data.map(item => sanitizeFormData(item)) as unknown as T;
    }
    if (data !== null && typeof data === 'object') {
        const sanitized: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
            sanitized[key] = sanitizeFormData(value);
        }
        return sanitized as T;
    }
    return data;
}

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Osobna instancja do wysylki plikow, bez domyslnego Content-Type.
// Przy FormData naglowek musi ustawic przegladarka, bo dopisuje do niego granice
// czesci (boundary) - bez niej serwer nie rozdzieli pol i widzi puste zadanie.
// Instancja powyzej narzuca 'application/json' kazdemu zadaniu, wiec do przesylania
// plikow sie nie nadaje.
const apiUpload = axios.create({ baseURL: BASE_URL });
async function rawGet<T>(url: string): Promise<T> {
    const response = await api.get<T>(url);
    return response.data;
}
// Uniwersalna funkcja do bezpiecznego pobierania danych
async function safeGet<T>(url: string): Promise<T> {
    try {
        const response = await api.get<T>(url);
        return response.data;
    } catch {
        return [] as unknown as T;
    }
}

export interface ContactDetails {
    firstName: string;
    lastName: string;
    email: string;
    phoneNo: string;
    companyName?: string;
}

export interface Token {
    value: string;
    url: string;
    validTill: string;
}

export interface FormResponse {
    formType: "PERSONAL" | "B2B";
    status: string;
    contactDetails: ContactDetails;
    token: Token;
}

export interface FormDataB2B {
    formType: "B2B";
    status: "SUBMITTED";
    language: FormLanguage;
    consents: FormConsents;
    contactDetails: ContactDetails;
    token: Token;
    name: string;
    taxId: string;
    statisticalId: string;
    legalForm: string;
    registeredOfficeAddress: {
        country: string;
        voivodeship: string;
        county: string;
        municipality: string;
        street: string;
        houseNumber: string;
        apartmentNumber?: string;
        postalCode: string;
        city: string;
    };
    representativePerson: {
        firstName: string;
        lastName: string;
        position: string;
    };
    settlementData: {
        accountNumber: string;
        bankName: string;
    };
    declarationFooter: {
        date: string;
    };
}

export interface TaxOfficeDict {
    value: string;
    label: string;
}

export interface FormDataPerson {
    formType: "PERSONAL";
    status: "SUBMITTED";
    language: FormLanguage;
    consents: FormConsents;
    contactDetails: ContactDetails;
    token: Token | '';
    personalData: {
        lastName: string;
        firstName: string;
        middleName: string;
        pesel: string;
        dateOfBirth: string;
        citizenship: string[];
        taxOfficeDict: TaxOfficeDict;
        // Nazwa urzędu — to pole zna backend BizForms (taxOfficeDict ignoruje), więc bez niego
        // urząd skarbowy nie trafiał ani do bazy, ani do FormsPortal.
        taxOffice: string;
    };
    residentialAddress: {
        country: string;
        voivodeship: string;
        county: string;
        municipality: string;
        street: string;
        houseNumber: string;
        apartmentNumber?: string;
        postalCode: string;
        city: string;
    };
    // Opcjonalne — sekcja oświadczeń ZUS bywa wyłączona (patrz ZUS_STATEMENTS_ENABLED
    // w FormPagePerson). Backend przyjmuje formularz bez tego pola.
    declaration?: {
        socialInsuranceFromOtherContract?: {
            entityName: string;
            salaryAboveMinimum: boolean;
        };
        contractOfMandateWithOtherEntity?: {
            nameAndAddress: string;
            salaryAboveMinimum: boolean;
            periodFrom: string;
            periodTo: string;
        };
        retired: boolean;
        pensioner: boolean;
        student: boolean;
        disability?: {
            haveDisability: boolean;
            degree: "lekki" | "umiarkowany" | "znaczny";
        };
        voluntaryHealthInsurance: boolean;
        businessActivity: boolean;
    };
    settlementData: {
        accountNumber: string;
        bankName: string;
    };
    declarationFooter: {
        date: string;
    };
}
export interface NipData {
    name: string;
    regon: string;
    statusVat: string;
    workingAddress: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    legalForm?: string;
}

export type FormLanguage = 'pl' | 'en';

/** Zgody zaznaczone w formularzu — wysyłane jawnie, bo trafiają do PDF-a potwierdzenia. */
export interface FormConsents {
    dataAgreement: boolean;
    personalDataProcessingConsent: boolean;
}

export interface SmsSendResponse {
    phoneMasked: string;
    expiresInSeconds: number;
    resendAfterSeconds: number;
}

export interface SmsVerifyResponse {
    proof: string;
    expiresInSeconds: number;
}

/** Ciało błędu z backendu (GlobalExceptionHandler): klucz + opcjonalne liczniki dla przepływu SMS. */
export interface ApiError {
    status: number;
    error: string;
    remainingAttempts?: number | null;
    retryAfterSeconds?: number | null;
}

/** Wyciąga ujednolicony błąd z wyjątku axiosa; gdy odpowiedź nie ma ciała (np. 429 z rate-limitera), zostaje sam status. */
export function toApiError(err: unknown): ApiError {
    if (axios.isAxiosError(err)) {
        const data = (err.response?.data ?? {}) as Partial<ApiError>;
        return {
            status: err.response?.status ?? 0,
            error: data.error ?? (err.response?.status === 429 ? 'too_many_requests' : 'network_error'),
            remainingAttempts: data.remainingAttempts,
            retryAfterSeconds: data.retryAfterSeconds,
        };
    }
    return { status: 0, error: 'unknown_error' };
}

/** Strona podpisu umowy (/f/sign/{token}) — informacje o dokumencie i uczestniku. */
export interface SignInfo {
    status: 'NEW' | 'SIGNED' | 'REJECTED' | 'CANCELLED';
    documentId: string;
    title: string;
    umowaNr: string;
    principalName: string;
    validTill: string;
    participant: { firstName: string; lastName: string; email: string; role: string; language: string; companyName?: string };
    signedAt?: string | null;
    phoneMasked?: string | null;
    rejectionReason?: string | null;
}

export const signingService = {
    getInfo: (token: string) => rawGet<SignInfo>(`/be/api/signing/${token}`),
    documentUrl: (token: string) => `/be/api/signing/${token}/document`,
    sendSmsCode: (token: string, language: FormLanguage) =>
        api.post<SmsSendResponse>(`/be/api/signing/${token}/sms/send`, { language }).then(r => r.data),
    verifySmsCode: (token: string, code: string) =>
        api.post<SmsVerifyResponse>(`/be/api/signing/${token}/sms/verify`, { code }).then(r => r.data),
    sign: (token: string, proof: string, language: FormLanguage) =>
        api.put<SignInfo>(`/be/api/signing/${token}`, { action: 'sign', accepted: true, language },
            { headers: { 'X-Signature-Proof': proof } }).then(r => r.data),
    // Odmowa też wymaga dowodu SMS — sam link (np. przekazany mail) nie wystarczy, by unieważnić proces.
    reject: (token: string, reason: string, proof: string, language: FormLanguage) =>
        api.put<SignInfo>(`/be/api/signing/${token}`, { action: 'reject', reason, language },
            { headers: { 'X-Signature-Proof': proof } }).then(r => r.data),
};

export const dictionaryService = {
    // Zapis formularza wymaga jednorazowego dowodu z verifySmsCode — bez niego backend odpowiada 403.
    postFormB2B: (token: string, data: FormDataB2B, proof: string) =>
        api.put(`/be/api/forms/${token}`, data, { headers: { 'X-Signature-Proof': proof } }),
    postFormPerson: (token: string, data: FormDataPerson, proof: string) =>
        api.put(`/be/api/forms/${token}`, data, { headers: { 'X-Signature-Proof': proof } }),
    // Zalacznik oswiadczenia (np. skan legitymacji). Wysylany PRZED zapisem formularza,
    // zeby nieudana wysylka pliku dala sie poprawic, zanim formularz zostanie podpisany.
    uploadStatementAttachment: (token: string, kind: string, file: File) => {
        const dane = new FormData();
        dane.append('kind', kind);
        dane.append('file', file);
        return apiUpload.post(`/be/api/forms/${token}/attachments`, dane);
    },
    sendSmsCode: (token: string, language: FormLanguage) =>
        api.post<SmsSendResponse>(`/be/api/forms/${token}/sms/send`, { language }).then(r => r.data),
    verifySmsCode: (token: string, code: string) =>
        api.post<SmsVerifyResponse>(`/be/api/forms/${token}/sms/verify`, { code }).then(r => r.data),
    getFormByToken: (token: string) => rawGet<FormResponse>(`/be/api/forms/${token}`),
    getCitizenships: () => safeGet<DictionaryItem[]>('/be/api/dictionary/citizenship'),
    getTaxOffices: () => safeGet<DictionaryItem[]>('/be/api/dictionary/tax-office'),
    getVoivodeships: () => safeGet<DictionaryName[]>('/be/api/dictionary/address/voivodeships'),
    getCounties: (voivodeshipKey: string) =>
        safeGet<DictionaryName[]>(`/be/api/dictionary/address/voivodeships/${voivodeshipKey}/counties`),
    getMunicipalities: (voivodeshipKey: string, countyKey: string) =>
        safeGet<DictionaryName[]>(`/be/api/dictionary/address/voivodeships/${voivodeshipKey}/counties/${countyKey}/municipalities`),
    getNipData: (nip: string) => rawGet<NipData>(`/be/api/nip/${nip}`),
};

export default dictionaryService;